"use client";

import { useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { useToolUi } from "@/components/tools/tool-ui";
import { WORKSHEET_COMMON_STRINGS } from "@/components/tools/worksheets-math/common-strings";
import { NumberLineSvg } from "@/components/tools/worksheets-math/number-line-svg";
import {
  ActionBar,
  defaultSettings,
  useCommonSettings,
  useWorksheetSeed,
  WorksheetSheets,
} from "@/components/tools/worksheets-math/worksheet-kit";
import { t } from "@/lib/i18n/interpolate";
import type { PageOrientation } from "@/lib/tool-kit/worksheets/layout";
import {
  buildLine,
  type FractionForm,
  hiddenLabels,
  type LabelMode,
  type LineError,
  type LineKind,
  MAX_TICKS,
  parseJumps,
  parsePoints,
  specFromInputs,
} from "@/lib/tool-kit/worksheets/numberline";
import { createRng } from "@/lib/tool-kit/worksheets/random";

/** English strings. A translated content file overrides these through `ui`. */
export const NUMBER_LINE_GENERATOR_STRINGS = {
  ...WORKSHEET_COMMON_STRINGS,
  defaultTitle: "Number Lines",
  kindLabel: "Number line type",
  kinds: {
    integer: "Whole numbers",
    decimal: "Decimals",
    fraction: "Fractions",
  },
  startLabel: "Start",
  endLabel: "End",
  stepLabel: "Distance between ticks",
  denominatorLabel: "Ticks per whole (denominator)",
  fractionFormLabel: "Fraction labels",
  fractionForms: {
    mixed: "Mixed numbers, simplified (1 1/4)",
    improper: "Improper fractions, simplified (5/4)",
    unreduced: "Same denominator throughout (2/4)",
  },
  labelEveryLabel: "Label every Nth tick (1 labels every tick)",
  revealLabel: "Labels shown",
  reveals: {
    all: "All labels",
    ends: "Only the first and last",
    q25: "Hide a quarter — fill in the boxes",
    q50: "Hide half — fill in the boxes",
    q75: "Hide three quarters — fill in the boxes",
    none: "No labels — empty boxes",
  },
  arrows: "Arrowheads at both ends",
  jumpsLabel: "Jumps (one per line, like 0 to 3)",
  jumpsPlaceholder: "0 to 3\n3 to 7",
  pointsLabel: "Points to mark (separate with semicolons or commas)",
  pointsPlaceholder: "2; 5; 8",
  pointLetters: "Name the points A, B, C …",
  linesLabel: "Number lines on the page",
  orientationLabel: "Page orientation",
  orientations: { landscape: "Landscape", portrait: "Portrait" },
  errors: {
    range: "Enter a start that is smaller than the end.",
    step: "The distance between ticks must divide the range evenly, such as 0 to 1 in steps of 0.1.",
    integer:
      "Use whole numbers for the start and end (and the step on a whole-number line).",
    denominator: "Choose a denominator from 2 to 20.",
    tooMany:
      "That makes more than {max} ticks. Use a larger step or a shorter range.",
  },
  rejected:
    "Not used, because they are unreadable or outside the line: {items}",
  lineLabel: "Number line from {start} to {end}",
} as const;

type Reveal = keyof typeof NUMBER_LINE_GENERATOR_STRINGS.reveals;

const REVEAL_MODE: Record<Reveal, { mode: LabelMode; share: number }> = {
  all: { mode: "all", share: 0 },
  ends: { mode: "ends", share: 0 },
  q25: { mode: "some", share: 0.25 },
  q50: { mode: "some", share: 0.5 },
  q75: { mode: "some", share: 0.75 },
  none: { mode: "none", share: 0 },
};
const DENOMINATORS = [2, 3, 4, 5, 6, 8, 10, 12, 16, 20];
const LABEL_EVERY = [1, 2, 3, 4, 5, 10];
const LINE_COUNTS = [1, 2, 3, 4, 5, 6, 8];
const DEFAULTS: Record<LineKind, { start: string; end: string; step: string }> =
  {
    integer: { start: "0", end: "10", step: "1" },
    decimal: { start: "0", end: "1", step: "0.1" },
    fraction: { start: "0", end: "2", step: "1" },
  };

export default function NumberLineGeneratorWidget() {
  const strings = useToolUi(NUMBER_LINE_GENERATOR_STRINGS);
  const [settings, update] = useCommonSettings(
    defaultSettings({ header: true, answerKey: true, count: 3 })
  );
  const seed = useWorksheetSeed();
  const [kind, setKind] = useState<LineKind>("integer");
  const [range, setRange] = useState(DEFAULTS.integer);
  const [denominator, setDenominator] = useState(4);
  const [form, setForm] = useState<FractionForm>("mixed");
  const [labelEvery, setLabelEvery] = useState(1);
  const [reveal, setReveal] = useState<Reveal>("all");
  const [arrows, setArrows] = useState(true);
  const [jumpText, setJumpText] = useState("");
  const [pointText, setPointText] = useState("");
  const [letters, setLetters] = useState(true);
  const [orientation, setOrientation] = useState<PageOrientation>("landscape");

  const spec = specFromInputs({
    kind,
    start: range.start,
    end: range.end,
    step: range.step,
    denominator,
    labelEvery,
    fractionForm: form,
  });
  const built = spec.ok ? buildLine(spec.spec) : spec;
  const error: LineError | null = built.ok ? null : built.error;
  const line = built.ok ? built.line : null;
  const jumps = line ? parseJumps(jumpText, line) : null;
  const points = line ? parsePoints(pointText, line) : null;
  const { mode, share } = REVEAL_MODE[reveal];
  const hasGaps = mode !== "all";
  const rng = createRng(seed.seed);
  const hiddenPerLine = line
    ? Array.from({ length: settings.count }, () =>
        hiddenLabels(line, mode, share, rng)
      )
    : [];
  const none = new Set<number>();
  const rejected = [...(jumps?.rejected ?? []), ...(points?.rejected ?? [])];
  const label = t(strings.lineLabel, { start: range.start, end: range.end });

  function switchKind(next: LineKind) {
    setKind(next);
    setRange(DEFAULTS[next]);
  }

  const drawLines = (hide: (index: number) => ReadonlySet<number>) => (
    <div className="space-y-5">
      {line
        ? Array.from({ length: settings.count }, (_, index) => (
            <div
              className="ws-avoid-break"
              // biome-ignore lint/suspicious/noArrayIndexKey: lines are positional
              key={index}
            >
              <NumberLineSvg
                arrows={arrows}
                hidden={hide(index)}
                jumps={jumps?.items ?? []}
                label={label}
                letters={letters}
                line={line}
                points={points?.items ?? []}
              />
            </div>
          ))
        : null}
    </div>
  );

  return (
    <div className="space-y-6 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <PillGroup
          label={strings.kindLabel}
          name="nl-kind"
          onChange={switchKind}
          options={(["integer", "decimal", "fraction"] as const).map(
            (value) => ({
              value,
              label: strings.kinds[value],
            })
          )}
          value={kind}
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <TextField
            id="nl-start"
            label={strings.startLabel}
            numeric
            onChange={(start) => setRange({ ...range, start })}
            value={range.start}
          />
          <TextField
            id="nl-end"
            label={strings.endLabel}
            numeric
            onChange={(end) => setRange({ ...range, end })}
            value={range.end}
          />
          {kind === "fraction" ? (
            <SelectField
              id="nl-denominator"
              label={strings.denominatorLabel}
              onChange={(value) => setDenominator(Number(value))}
              options={DENOMINATORS.map((value) => ({
                value: String(value),
                label: String(value),
              }))}
              value={String(denominator)}
            />
          ) : (
            <TextField
              id="nl-step"
              label={strings.stepLabel}
              numeric
              onChange={(step) => setRange({ ...range, step })}
              value={range.step}
            />
          )}
          <SelectField
            id="nl-label-every"
            label={strings.labelEveryLabel}
            onChange={(value) => setLabelEvery(Number(value))}
            options={LABEL_EVERY.map((value) => ({
              value: String(value),
              label: String(value),
            }))}
            value={String(labelEvery)}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {kind === "fraction" ? (
            <SelectField
              id="nl-form"
              label={strings.fractionFormLabel}
              onChange={(value: FractionForm) => setForm(value)}
              options={(["mixed", "improper", "unreduced"] as const).map(
                (value) => ({
                  value,
                  label: strings.fractionForms[value],
                })
              )}
              value={form}
            />
          ) : null}
          <SelectField
            id="nl-reveal"
            label={strings.revealLabel}
            onChange={(value: Reveal) => setReveal(value)}
            options={(Object.keys(REVEAL_MODE) as Reveal[]).map((value) => ({
              value,
              label: strings.reveals[value],
            }))}
            value={reveal}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextAreaField
            id="nl-jumps"
            label={strings.jumpsLabel}
            onChange={setJumpText}
            placeholder={strings.jumpsPlaceholder}
            rows={3}
            value={jumpText}
          />
          <div className="space-y-3">
            <TextField
              id="nl-points"
              label={strings.pointsLabel}
              onChange={setPointText}
              placeholder={strings.pointsPlaceholder}
              value={pointText}
            />
            <Checkbox
              checked={letters}
              label={strings.pointLetters}
              onChange={setLetters}
            />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SelectField
            id="nl-count"
            label={strings.linesLabel}
            onChange={(value) => update({ count: Number(value) })}
            options={LINE_COUNTS.map((value) => ({
              value: String(value),
              label: String(value),
            }))}
            value={String(settings.count)}
          />
          <SelectField
            id="nl-orientation"
            label={strings.orientationLabel}
            onChange={(value: PageOrientation) => setOrientation(value)}
            options={(["landscape", "portrait"] as const).map((value) => ({
              value,
              label: strings.orientations[value],
            }))}
            value={orientation}
          />
          <SelectField
            id="nl-paper"
            label={strings.paperLabel}
            onChange={(paper) => update({ paper })}
            options={(["letter", "a4"] as const).map((value) => ({
              value,
              label: strings.papers[value],
            }))}
            value={settings.paper}
          />
          <TextField
            hint={strings.titleHint}
            id="nl-title"
            label={strings.titleLabel}
            onChange={(title) => update({ title })}
            value={settings.title}
          />
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={arrows}
            label={strings.arrows}
            onChange={setArrows}
          />
          <Checkbox
            checked={settings.header}
            label={strings.showHeader}
            onChange={(header) => update({ header })}
          />
          {hasGaps ? (
            <Checkbox
              checked={settings.answerKey}
              label={strings.answerKey}
              onChange={(answerKey) => update({ answerKey })}
            />
          ) : null}
        </div>
        {error ? (
          <ErrorNotice>
            {t(strings.errors[error], { max: String(MAX_TICKS) })}
          </ErrorNotice>
        ) : null}
        {rejected.length > 0 ? (
          <Notice>{t(strings.rejected, { items: rejected.join("; ") })}</Notice>
        ) : null}
        <ActionBar
          idPrefix="nl"
          seed={seed}
          showSeed={mode === "some"}
          strings={strings}
        />
      </div>
      {line ? (
        <WorksheetSheets
          answers={drawLines(() => none)}
          orientation={orientation}
          problems={drawLines((index) => hiddenPerLine[index] ?? none)}
          seed={seed.seed}
          settings={{ ...settings, answerKey: settings.answerKey && hasGaps }}
          showSeed={mode === "some"}
          strings={strings}
          title={settings.title.trim() || strings.defaultTitle}
        />
      ) : null}
    </div>
  );
}
